import { Component, inject, Input, OnInit } from '@angular/core';
import { CardModule, DirectivesModule } from 'rcp-common-lib';
import { AgingTrendMetricSummaryRequest } from '@core/generated/openapi';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthService } from '@services/auth/auth.service';
import { ArSnapshotService } from '@services/ar-snapshot/ar-snapshot.service';
import { ClaimStalledFilterService } from '@services/claim-stalled-filter/claim-stalled-filter.service';
import { skip } from 'rxjs';

enum TREND_DIRECTION {
    NEGATIVE,
    NONE,
    POSITIVE,
}

@UntilDestroy()
@Component({
    selector: 'rcp-oup-aging-trend-summary-metric',
    imports: [CardModule, DirectivesModule],
    templateUrl: './aging-trend-summary-metric.component.html',
    styleUrl: './aging-trend-summary-metric.component.scss',
})
export class AgingTrendSummaryMetricComponent implements OnInit {
    private readonly claimStalledFilterService = inject(
        ClaimStalledFilterService,
    );

    @Input() claimStalledFilterId: string;

    isLoading: boolean = false;
    locations: number[] = [];

    age30PlusPercent: number;
    age30PlusTrend: TREND_DIRECTION;
    age30PlusDifference: string;
    age30DifferenceClass: string;
    age60PlusPercent: number;
    age60PlusTrend: TREND_DIRECTION;
    age60PlusDifference: string;
    age60DifferenceClass: string;

    constructor(
        private authService: AuthService,
        private arSnapshotService: ArSnapshotService,
    ) {}

    ngOnInit() {
        this.getSelectedLocations();
        this.onStalledFilterChange();
    }

    getSelectedLocations() {
        this.authService.agingLocationFilter
            .pipe(untilDestroyed(this))
            .subscribe((locations) => {
                if (this.authService.isAgingUploadByLocationOff(locations)) {
                    this.locations = [];
                    this.loadData();
                } else if (
                    Array.isArray(locations) &&
                    !locations.every((val) => val === undefined)
                ) {
                    this.locations = locations;
                    this.loadData();
                } else if (
                    Array.isArray(locations) &&
                    locations.every((val) => val === undefined)
                ) {
                    this.loadData();
                }
            });
    }

    onStalledFilterChange() {
        if (this.claimStalledFilterId) {
            this.claimStalledFilterService
                .onFilterStatusChange(this.claimStalledFilterId)
                .pipe(untilDestroyed(this), skip(1))
                .subscribe({
                    next: () => this.loadData(),
                });
        }
    }

    loadData() {
        this.isLoading = true;
        this.arSnapshotService
            .findARSnapshotAgingTrendMetricSummary(this.getReportRequest())
            .pipe(untilDestroyed(this))
            .subscribe((data) => {
                this.age30PlusPercent = Math.round(data.age30PlusPercent);
                this.age30PlusDifference = this.getDifferenceText(
                    data.age30PlusDifference,
                );
                this.age30PlusTrend = this.calculateTrendDirection(
                    Math.round(data.age30PlusDifference),
                );
                this.age30DifferenceClass = this.getDifferenceClass(
                    this.age30PlusTrend,
                );

                this.age60PlusPercent = Math.round(data.age60PlusPercent);
                this.age60PlusDifference = this.getDifferenceText(
                    data.age60PlusDifference,
                );
                this.age60PlusTrend = this.calculateTrendDirection(
                    Math.round(data.age60PlusDifference),
                );
                this.age60DifferenceClass = this.getDifferenceClass(
                    this.age60PlusTrend,
                );

                this.isLoading = false;
            });
    }

    getDifferenceText(difference: number): string {
        const wholeDiff = Math.round(difference);

        if (wholeDiff === 0) {
            return 'No Change';
        } else {
            return wholeDiff.toString() + '%';
        }
    }

    calculateTrendDirection(difference: number): TREND_DIRECTION {
        if (difference === 0) {
            return TREND_DIRECTION.NONE;
        }

        return difference < 0
            ? TREND_DIRECTION.NEGATIVE
            : TREND_DIRECTION.POSITIVE;
    }

    getDifferenceClass(trend: TREND_DIRECTION): string {
        switch (trend) {
            case TREND_DIRECTION.NEGATIVE:
                return 'text-green-600';
            case TREND_DIRECTION.NONE:
                return 'text-sm text-gray-500';
            case TREND_DIRECTION.POSITIVE:
                return 'text-red-600';
        }
    }

    getReportRequest(): AgingTrendMetricSummaryRequest {
        return {
            accountLocationIds: this.locations,
            nonStalledClaimsOnly:
                !this.claimStalledFilterService.getFilterStatusValue(
                    this.claimStalledFilterId,
                ),
        };
    }

    protected readonly TREND_DIRECTION = TREND_DIRECTION;
}
